import type * as React from 'react';
import { Text, type ViewStyle } from 'react-native';

import { flex, spacing } from '../constants';
import { NonEditableView } from '../DragAndDrop';
import { Interactive, type InteractiveState } from '../Interactive';
import { ThemedStyleSheet, themedFonts, useCurrentTheme } from '../theme';
import { type CodeBlockLineHighlighting, highlightingThemedStyles } from './line-highlighting';

export interface CodeBlockLineNumberProps {
    number: number;
    highlighting?: CodeBlockLineHighlighting;
    onPress?: () => void;
    gutterInteractiveState: InteractiveState;
}

export const CODEBLOCK_LINE_GUTTER_WIDTH = 48;

const interactiveHandle = Interactive.createInteractiveHandle('codeblock-line-number');

/**
 * Gutter for a code block line with the number.
 */
export function CodeBlockLineNumber(props: CodeBlockLineNumberProps): React.ReactElement {
    const { number, highlighting, gutterInteractiveState, onPress } = props;

    const [, font] = useCurrentTheme(themedFonts);
    const [, styles] = useCurrentTheme(themedStyles);
    const [, highlightingStyles] = useCurrentTheme(
        // @ts-ignore
        highlighting ? highlightingThemedStyles[highlighting] : undefined
    );

    const TextComponent = onPress ? Interactive.Text.Child : Text;
    const textContent = (
        <TextComponent
            style={[font.Code, font.textMuted, font.AlignRight, highlightingStyles?.text]}
            {...(onPress
                ? {
                      handle: interactiveHandle,
                      hoverStyle: font.text,
                  }
                : {})}
        >
            {number}
        </TextComponent>
    );

    const contentStyles = [styles.lineNumberContent, highlightingStyles?.line];
    const content = onPress ? (
        <Interactive.TouchableOpacity
            handle={interactiveHandle}
            onPress={onPress}
            style={contentStyles}
            {...gutterInteractiveState.events}
        >
            {textContent}
        </Interactive.TouchableOpacity>
    ) : (
        textContent
    );

    return (
        <NonEditableView style={[styles.lineNumberWrapper, onPress ? flex.row : contentStyles]}>
            {content}
        </NonEditableView>
    );
}

const themedStyles = ThemedStyleSheet.create({
    lineNumberWrapper: (theme) =>
        // @ts-ignore position 'sticky' isn't a valid value
        ({
            flexGrow: 0,
            flexShrink: 0,
            width: CODEBLOCK_LINE_GUTTER_WIDTH,
            // fix the line numbers on the left when scrolling
            // and make sure the text goes under them
            position: 'sticky',
            left: 0,
            backgroundColor: theme.colors.background.muted,
            zIndex: 1,
        }) as ViewStyle,
    lineNumberContent: {
        width: CODEBLOCK_LINE_GUTTER_WIDTH,
        paddingRight: spacing.small,
    },
});
